import { RecentOrdersStyledDataGrid } from "./RecentOrdersStyledDataGrid";
import { GridColDef, GridRenderCellParams, GridRowsProp } from "@mui/x-data-grid";
import { Box, Typography, useMediaQuery, useTheme, CircularProgress } from "@mui/material";
import { toLocaleDateTime, useTranslation } from "@chr/online-shared-ui-localizations";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { RecentOrder, Stop, UserWidget } from "@/common/constants/online.homepagegateway.api";
import "./RecentOrdersWidget.scss";
import { StatusTag, StatusTagProps, Tooltip } from "@chr/eds-react";
import { useAppConfig } from "@/common/utilities/UseAppConfig";
import { userRecentOrders } from "@/common/apis/recentOrders";
import LocationInfo from "./LocationInfo";
import React from "react";
import DataGridPagination from "../../Pagination/GridPagination";
import EmptyStateBox from '@/common/assets/img/EmptyStateBox.svg';

interface RecentOrdersWidgetProps {
    userWidget: UserWidget;
    triggerResize: () => void;
}

const LoadingSpinner = () => (
    <Box className="loading-state"
        data-testid="loading-spinner"
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px'
        }}>
        <CircularProgress />
    </Box>
);

const ProcessingDelayedTooltip: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const translate = useTranslation();
    const tooltipContent = translate("Orders are processing slower than usual—thanks for your patience.");

    return (
        <Tooltip
            title={tooltipContent}
            position="bottom"
        >
            {children}
        </Tooltip>
    );
};

export const RecentOrdersWidget: React.FC<RecentOrdersWidgetProps> = ({
    userWidget,
    triggerResize
}) => {
    const PROCESSING_DELAYED = "Processing delayed";
    const translate = useTranslation();
    const { orderDetailUri } = useAppConfig();
    const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    let firstLoad = true;
    let timeoutId: any;
    const theme = useTheme();
    const isSmOrSmaller = useMediaQuery(theme.breakpoints.down('md'));

    const getRecentOrders = async () => {
        if (firstLoad) {
            setIsLoading(true);
            firstLoad = false;
        }
        try {
            const recentOrderQueryResult: RecentOrder[] = await userRecentOrders();
            setRecentOrders(recentOrderQueryResult);
        } catch (error) {
            setRecentOrders([]);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getRecentOrders();
        const interval = setInterval(() => getRecentOrders(), 60000)
        return () => {
          clearInterval(interval);
        }
    }, []);

    useLayoutEffect(() => {
        // if we have state changes, that we think will affect the layout of the widget (can cause changes in height), add it to this dep array so widgets won't overlap
        triggerResize();
    }, [isLoading])

    const handleOrderNumberClick = (recentOrder: RecentOrder) => {
        let url: string;
        if (recentOrder.sourceSystem === "NonNav") {
            url = `${orderDetailUri}${recentOrder.orderNumber}/${recentOrder.jobKey}`;
        } else {
            url = `${orderDetailUri}${recentOrder.orderNumber}`;
        }
        window.location.assign(url);
    }

    const getPrimaryRefNumbersDisplay = (recentOrder: any) => {
        if (recentOrder.primaryReferenceNumbers.length > 0){
            return recentOrder.primaryReferenceNumbers.join(", ");
        }
        if (recentOrder.orderNumber !== undefined && recentOrder?.orderNumber !== null && `${recentOrder.orderNumber}` !== '') {
            return recentOrder.orderNumber;
        }
        return 'TBD';
    }

    const renderOrderNumberColumn = (params: GridRenderCellParams) => {
        const recentOrder = params.row;
        const newDate = new Date(recentOrder.lastViewedDate);
        const lastViewedText = `${translate('Last viewed on')} ${toLocaleDateTime(newDate, { dateStyle: 'medium' })}`;
    
        return (
            <div className="order-number-column">
                {renderOrderNumberSpan(recentOrder)}
                <span className="last-viewed-span" title={lastViewedText}>
                    {lastViewedText}
                </span>
            </div>
        )
    }

    const renderOrderNumberSpan = (recentOrder: any, isSmallRow: boolean = false) => {
        const primaryReferences = getPrimaryRefNumbersDisplay(recentOrder);
        let title = primaryReferences;
        if (isSmallRow){
            title = undefined;
        }

        return (
            <React.Fragment>
                { recentOrder.status === PROCESSING_DELAYED ? (
                    <p className="order-number-span processing-delayed"
                    title={primaryReferences}
                    data-testid={`order-number-${recentOrder.id}`}
                    >
                        {primaryReferences}
                    </p>
                ) : (
                    <p className="order-number-span"
                    title={primaryReferences}
                    data-testid={`order-number-${recentOrder.id}`}
                    onClick={() => handleOrderNumberClick(recentOrder)}
                    >
                        {primaryReferences}
                    </p>
                )}
            </React.Fragment>
        )
    }

    const renderStatusColumn = (params: GridRenderCellParams) => {
        const recentOrder = params.row;

        return (
            <div className="status-column">
                {renderStatusSpan(recentOrder)}
            </div>
        )
    }

    const renderStatusSpan = (recentOrder: any) => {
        let label = "";
        let severity: StatusTagProps["severity"] = 'neutral';
        if (recentOrder.riskLevel === "Late") {
            label = translate('Late');
            severity = 'negative';
        } else if (recentOrder.riskLevel === "At Risk") {
            label = translate('At Risk');
            severity = 'risky';
        } else if (recentOrder.riskLevel === "On Time") {
            label = translate('On Time');
            severity = 'positive';
        } else if (recentOrder.riskLevel === "Early") {
            label = translate('Early');
            severity = 'urgent';
        }

        let statusClassName = "status-span";
        if (recentOrder.status === PROCESSING_DELAYED) {
            statusClassName += " processing-delayed";
        }

        const statusElement = (
            <p className={statusClassName}>
                {recentOrder.status}
            </p>
        );

        return (
            <React.Fragment>
                {recentOrder.status === PROCESSING_DELAYED ? (
                    <ProcessingDelayedTooltip>
                        {statusElement}
                    </ProcessingDelayedTooltip>
                ) : (
                    statusElement
                )}
                <span className="status-icon-span">
                    { label != '' &&
                        <StatusTag
                            label={label}
                            severity={severity}
                            size="compact"
                            labelSize="small"
                            icon={true}
                        />
                    }
                </span>
            </React.Fragment>
        );
    }

    const renderFromColumn = (params: GridRenderCellParams) => {
        return <LocationInfo stops={params.row.pick} />;
    }

    const renderToColumn = (params: GridRenderCellParams) => {
        return <LocationInfo stops={params.row.drop} />;
    }

    const getUniqueStops = (stops: Stop[]) => {
        const uniqueStops: Stop[] = [];
        let id = 1;

        if (!stops || stops.length == 0) {
            return uniqueStops;
        }

        stops.forEach(stop => {
            const index = uniqueStops.findIndex(each => getFilterCriteria(each) == getFilterCriteria(stop));

            if (index >= 0) {
                const newStop = uniqueStops[index];
                newStop.actualDate = stop.actualDate;
                newStop.scheduledDate = stop.scheduledDate;
                newStop.requestedDate = stop.requestedDate;
                uniqueStops[index] = newStop;
            } else {
                stop.id = id;
                uniqueStops.push(stop);
                id += 1;
            }
        });

        return uniqueStops;
    }

    const getFilterCriteria = (stop: Stop) => {
        return `${stop.stopType} ${stop.locationName} ${stop.city} ${stop.stateProvinceName}`;
    }

    const renderSmallRow = (params: GridRenderCellParams) => {
        const recentOrder = params.row;
        const newDate = new Date(recentOrder.lastViewedDate);

        return (
            <React.Fragment>
                <div className="order-number-column">
                    {renderOrderNumberSpan(recentOrder, true)}
                </div>
                <div className="status-column">
                    {renderStatusSpan(recentOrder)}
                </div>
                <LocationInfo stops={params.row.pick}/>
                <LocationInfo stops={params.row.drop}/>
                <span className="last-viewed-span">
                    {translate('Last viewed on')} {toLocaleDateTime(newDate, { dateStyle: 'medium' })}
                </span>
            </React.Fragment>
        )
    }

    const getColumns = useCallback(() => {
        let columns: GridColDef[];

        if (isSmOrSmaller) {
            columns = [
                {
                    field: 'hiddenHeader',
                    headerName: translate(''),
                    flex: 2.5,
                    minWidth: 120,
                    headerAlign: 'left',
                    renderCell: renderSmallRow,
                }
            ];
        } else {
            columns = [
              {
                field: 'lastViewedDate',
                headerName: translate('Order'),
                flex: 2.5,
                minWidth: 120,
                headerAlign: 'left',
                renderCell: renderOrderNumberColumn,
              },
              {
                field: 'status',
                headerName: translate('Status'),
                flex: 2,
                minWidth: 120,
                headerAlign: 'left',
                renderCell: renderStatusColumn,
                sortComparator: (v1, v2) => v1.localeCompare(v2)
              },
              {
                field: 'from',
                headerName: translate('From'),
                flex: 3,
                minWidth: 120,
                headerAlign: 'left',
                renderCell: renderFromColumn,
                sortable: false
              },
              {
                field: 'to',
                headerName: translate('To'),
                flex: 3,
                minWidth: 120,
                headerAlign: 'left',
                renderCell: renderToColumn,
                sortable: false
              }
            ];
        }

        return columns;
    }, [isSmOrSmaller, translate])

    const getRows = useCallback(() => {
        const rows: any[] = [];
        recentOrders.forEach((os: RecentOrder, index: number) => {
          const uniqueStops = getUniqueStops(os.stops);

          const picks = uniqueStops
            .filter(a => a.stopType?.toLowerCase() === 'pick')
            .sort((a, b) => (a.stopNumber ?? 0) - (b.stopNumber ?? 0));

          const sortedDrops = uniqueStops
            .filter(a => a.stopType?.toLowerCase() === 'drop')
            .sort((a, b) => (a.stopNumber ?? 0) - (b.stopNumber ?? 0));

          // Rearrange to display the last drop first to match display order for OrderSearch and OrderDetail UIs
          const drops = [...sortedDrops.splice(-1), ...sortedDrops];

          rows.push({
                id: index + 1,
                orderNumber: os.orderNumber,
                primaryReferenceNumbers: os.primaryReferenceNumbers,
                status: os.status,
                lastViewedDate: os.lastViewedDate,
                riskLevel: os.riskLevel,
                sourceSystem: os.sourceSystem,
                jobKey: os.jobKey,
                pick: picks,
                drop: drops,
            });
        });
        rows.sort((a, b) => {
            if (a.status === PROCESSING_DELAYED || b.status === PROCESSING_DELAYED) {
                if (a.status === b.status) {
                    return new Date(b.lastViewedDate).getTime() - new Date(a.lastViewedDate).getTime();
                }
                if (a.status === PROCESSING_DELAYED) return -1;
                if (b.status === PROCESSING_DELAYED) return 1;
            }
            return new Date(b.lastViewedDate).getTime() - new Date(a.lastViewedDate).getTime();
        });
        return rows as GridRowsProp[];
    }, [recentOrders]);

    const onGridResize = ((params: any) => {
        if (params && params.height){
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                triggerResize()
            }, 500)
        }
    })

    const getGrid = useCallback(() => {
        return (
            <RecentOrdersStyledDataGrid
                rows={getRows()}
                columns={getColumns()}
                sortingOrder={['asc', 'desc']}
                initialState={{
                    sorting: {
                        sortModel:[]
                    },
                    pagination: {
                        paginationModel: {
                            page: 0,
                            pageSize: 10
                        }
                    }
                }}
                pageSizeOptions={[10]}
                pagination
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                disableDensitySelector
                disableRowSelectionOnClick
                autosizeOnMount
                hideFooterSelectedRowCount
                columnHeaderHeight={40}
                getRowHeight={() => 'auto'}
                getEstimatedRowHeight={() => 110}
                autoHeight
                disableVirtualization
                onResize={onGridResize}
                slots={{
                    pagination: DataGridPagination
                }}
            />
        );
    }, [getRows, getColumns, onGridResize]);

    return (
        <Box className="recent-order-widget">
           {(() => {
                        if (isLoading) {
                            return <LoadingSpinner />;
                        }
                        if (recentOrders.length === 0) {
                            return (
                                <Box className="empty-state">
                                    {!isSmOrSmaller && (
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>{translate('Order')}</th>
                                                    <th>{translate('Status')}</th>
                                                    <th>{translate('From')}</th>
                                                    <th>{translate('To')}</th>
                                                </tr>
                                            </thead>
                                        </table>
                                    )}
                                    <img src={EmptyStateBox} alt="Empty state" />
                                    <Typography variant="subtitle2">
                                        {translate('You have not viewed any orders yet')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {translate(
                                            'Your recently viewed orders will appear here for so you can easily view them again in the future.'
                                        )}
                                    </Typography>
                                </Box>
                            );
                        }
                        return getGrid();
                    })()}
        </Box>
    );
};

export default RecentOrdersWidget;
